import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from '../../shared/local-storage/local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class RdsService {

  private readonly RDS_API_PATH = '/api/v1/rds';

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService
  ) { }

  get country(): string {
    return this.localStorageService.getRegionSeleccionada?.getCode()?.toUpperCase();
  }

  get isVip(): boolean {
    return this.localStorageService.isMenuVip();
  }

  /**
   * Obtiene la configuración (config) por defecto.
   * En ambiente local retorna "Default" (para mantener compatibilidad con mocks)
   * En otros ambientes retorna "RDS_Default"
   */
  private getDefaultConfig(): string {
    const baseConfig = this.localStorageService.GPC_CONFIG.config.default;

    // En local, usar config simple para mantener compatibilidad con mocks existentes
    if (String(environment.IS_LOCAL.toLowerCase()) === "true") {
      return baseConfig;
    }

    return `RDS_${baseConfig}`;
  }

  private getDefaultConfigMenu(): string {
    const baseConfig = this.localStorageService.GPC_CONFIG.config.default;

    // En local, usar config simple para mantener compatibilidad con mocks existentes
    if (String(environment.IS_LOCAL.toLowerCase()) === "true") {
      return baseConfig;
    }

    return `MENU_${baseConfig}`;
  }



  /**
   * Construye la URL base considerando microservicios y VIP/Interno
   */
  private buildBaseUrl(): string {
    const rutaMicroservicio = (environment.MICROSERVICIO.toString().toLowerCase() === "true")
      ? "/" + this.localStorageService.configInit["F_URL_MS_IM_GCP"]
      : "";

    const urlBaseApi = this.isVip
      ? this.localStorageService.configInit["F_OKTA_URL_BASE_API"]
      : this.localStorageService.configInit["F_PINGID_URL_BASE_API"];

    return `${urlBaseApi}${rutaMicroservicio}`;
  }

  // ========================================
  // RDS MENU - Endpoints (sin collection)
  // ========================================

  /**
   * Carga RDS Menu
   * POST /api/v1/rds/menu/charge/{region}/{config}
   */
  chargeRdsMenu(config: string, body: any): Observable<any> {
    if (!config) {
      config = this.getDefaultConfigMenu();
      console.warn('[RdsService.chargeRdsMenu] No se proporcionó config, usando default:', config);
    }

    const baseUrl = this.buildBaseUrl();
    const url = `${baseUrl}${this.RDS_API_PATH}/menu/charge/${this.country}/${config}`;

    console.log('[RdsService.chargeRdsMenu] URL:', url);
    return this.http.post<any>(url, body).pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
  }

  /**
   * Consulta RDS Menu
   * GET /api/v1/rds/menu/{region}/{config}
   */
  getRdsMenu(): Observable<any> {

    const config = this.getDefaultConfigMenu();

    if (String(environment.IS_LOCAL.toLowerCase()) === "true") {
      const url = `assets/mock/${this.country.toLowerCase()}/rds/menu_${config}.json`;
      return this.http.get<any>(url).pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
    }

    const baseUrl = this.buildBaseUrl();

    // Usar el nuevo endpoint de RDS Menu
    const url = `${baseUrl}${this.RDS_API_PATH}/menu/${this.country}/${config}`;

    console.log('[RdsService.getRdsMenu] URL:', url);
    console.log('[RdsService.getRdsMenu] Region:', this.country, 'Config:', config);
    return this.http.get<any>(url).pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
  }

  /**
   * Eliminar item de RDS Menu
   * DELETE /api/v1/rds/menu/{region}/{config}/items/{item}
   */
  deleteRdsMenuItem(config: string, itemName: string): Observable<any> {
    if (!config) {
      config = this.getDefaultConfig();
      console.warn('[RdsService.deleteRdsMenuItem] No se proporcionó config, usando default:', config);
    }

    const baseUrl = this.buildBaseUrl();
    const url = `${baseUrl}${this.RDS_API_PATH}/menu/${this.country}/${config}/items/${itemName}`;

    console.log('[RdsService.deleteRdsMenuItem] URL:', url);
    return this.http.delete<any>(url).pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
  }

  /**
   * Consulta de configuraciones de RDS Menu
   * GET /api/v1/rds/menu/configs/{region}
   */
  getRdsMenuConfigs(): Observable<any> {
    if (String(environment.IS_LOCAL.toLowerCase()) === "true") {
      const url = `assets/mock/${this.country.toLowerCase()}/rds/menu_configs.json`;
      return this.http.get<any>(url).pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
    }

    const baseUrl = this.buildBaseUrl();
    const url = `${baseUrl}${this.RDS_API_PATH}/menu/configs/${this.country}`;

    console.log('[RdsService.getRdsMenuConfigs] URL:', url);
    return this.http.get<any>(url).pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
  }

  // ========================================
  // RDS GFD - Endpoints (CON collection)
  // ========================================

  /**
   * Carga RDS GFD
   * POST /api/v1/rds/gfd/charge/{region}/{collection}/{config}
   */
  chargeRdsGfd(collection: string, config: string, body: any): Observable<any> {
    if (!config) {
      config = this.getDefaultConfig();
      console.warn('[RdsService.chargeRdsGfd] No se proporcionó config, usando default:', config);
    }

    const baseUrl = this.buildBaseUrl();
    const url = `${baseUrl}${this.RDS_API_PATH}/gfd/charge/${this.country}/${collection}/${config}`;

    console.log('[RdsService.chargeRdsGfd] URL:', url);
    console.log('[RdsService.chargeRdsGfd] Collection:', collection, 'Config:', config);
    return this.http.post<any>(url, body).pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
  }

  /**
   * Consulta RDS GFD
   * GET /api/v1/rds/gfd/{region}/{collection}/{config}
   */
  getRdsGfd(collection: string, config: string): Observable<any> {
    if (!config) {
      config = this.getDefaultConfig();
      console.warn('[RdsService.getRdsGfd] No se proporcionó config, usando default:', config);
    }

    if (String(environment.IS_LOCAL.toLowerCase()) === "true") {
      const url = `assets/mock/${this.country.toLowerCase()}/rds/gfd_${collection}_${config}.json`;
      return this.http.get<any>(url).pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
    }

    const baseUrl = this.buildBaseUrl();
    const url = `${baseUrl}${this.RDS_API_PATH}/gfd/${this.country}/${collection}/${config}`;

    console.log('[RdsService.getRdsGfd] URL:', url);
    console.log('[RdsService.getRdsGfd] Collection:', collection, 'Config:', config);
    return this.http.get<any>(url).pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
  }

  /**
   * Eliminar item/campo de RDS GFD
   * DELETE /api/v1/rds/gfd/{region}/{collection}/{config}/items/{item}
   */
  deleteRdsGfdItem(collection: string, config: string, itemName: string): Observable<any> {
    if (!config) {
      config = this.getDefaultConfig();
      console.warn('[RdsService.deleteRdsGfdItem] No se proporcionó config, usando default:', config);
    }

    const baseUrl = this.buildBaseUrl();
    const url = `${baseUrl}${this.RDS_API_PATH}/gfd/${this.country}/${collection}/${config}/items/${itemName}`;

    console.log('[RdsService.deleteRdsGfdItem] URL:', url);
    console.log('[RdsService.deleteRdsGfdItem] Collection:', collection, 'Config:', config, 'Item:', itemName);
    return this.http.delete<any>(url).pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
  }

  /**
   * Consulta de configuraciones de RDS GFD por colección
   * GET /api/v1/rds/gfd/configs/{region}/{collection}
   */
  getRdsGfdConfigs(collection: string): Observable<any> {
    if (String(environment.IS_LOCAL.toLowerCase()) === "true") {
      const url = `assets/mock/${this.country.toLowerCase()}/rds/gfd_${collection}_configs.json`;
      return this.http.get<any>(url).pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
    }

    const baseUrl = this.buildBaseUrl();
    const url = `${baseUrl}${this.RDS_API_PATH}/gfd/configs/${this.country}/${collection}`;

    console.log('[RdsService.getRdsGfdConfigs] URL:', url);
    console.log('[RdsService.getRdsGfdConfigs] Collection:', collection);
    return this.http.get<any>(url).pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
  }
}

